import React from 'react';
import { Link } from 'react-router-dom';

import Banner from './Banner';
import { EXAMPLE_WHEEL, ExampleWorkbookLink } from './exampleFiles';
import { STATUS_LABEL } from './copy';
import {
  MiniWheel, ScoreBar, ScoreGauge, StatusIcon, SystemIcon, ZAxis, ZScoreBar,
} from './report/Visuals';


const STEPS = [
  {
    icon: 'blood',
    title: 'Blood',
    text: 'A blood sample is taken and its plasma is measured on the Olink Explore HT panel, which covers 5,348 proteins.',
  },
  {
    icon: 'protein',
    title: 'Proteins',
    text: 'ProteoRisk finds the proteins it was built on, up to 2,765 of the 5,348, and adjusts each level for your age, sex and BMI.',
  },
  {
    icon: 'pattern',
    title: 'Patterns',
    text: 'Your protein pattern is compared with the patterns seen in data from the general population.',
  },
  {
    icon: 'conditions',
    title: 'Conditions',
    text: 'You get a score from 0 to 100 for each of 129 results, grouped as low, moderate or high.',
  },
];

function Steps() {
  return (
    <ol className="PROTEORISK-flow">
      {STEPS.map((step, index) => (
        <li key={step.title} className="PROTEORISK-flow-step">
          <span className="PROTEORISK-flow-icon"><SystemIcon code={step.icon} size={30} /></span>
          <span className="PROTEORISK-flow-number">{index + 1}</span>
          <h3>{step.title}</h3>
          <p>{step.text}</p>
          {index < STEPS.length - 1 && <i className="fas fa-arrow-right PROTEORISK-flow-arrow" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  );
}

function GetTiles() {
  return (
    <div className="PROTEORISK-get-grid">
      <div className="PROTEORISK-get-tile">
        <div className="PROTEORISK-get-visual">
          <MiniWheel systems={EXAMPLE_WHEEL} />
        </div>
        <h3>Body systems at a glance</h3>
        <p>A wheel of 12 body systems shows where your proteins look typical and where there is a pattern worth discussing.</p>
      </div>
      <div className="PROTEORISK-get-tile">
        <div className="PROTEORISK-get-visual PROTEORISK-get-gauges">
          <ScoreGauge score100={84} tier="high" size={92} />
          <ScoreGauge score100={57} tier="moderate" size={92} />
        </div>
        <h3>Report highlights</h3>
        <p>Your strongest findings come first, each with a score from 0 to 100.</p>
      </div>
      <div className="PROTEORISK-get-tile">
        <div className="PROTEORISK-get-visual PROTEORISK-get-list">
          {[
            ['Hypertension', 84, 'high'],
            ['Lipid metabolism', 63, 'moderate'],
            ['Angina', 11, 'low'],
          ].map(([name, score, tier]) => (
            <div key={name} className="PROTEORISK-get-list-row">
              <span className={`PROTEORISK-get-dot ${tier}`} />
              <span className="PROTEORISK-get-list-name">{name}</span>
              <ScoreBar score100={score} tier={tier} />
              <span className={`PROTEORISK-get-list-score ${tier}`}>{score}</span>
            </div>
          ))}
        </div>
        <h3>Every condition, by body system</h3>
        <p>Current findings and 10-year risk, grouped by body system, with moderate and high results on top.</p>
      </div>
      <div className="PROTEORISK-get-tile">
        <div className="PROTEORISK-get-visual PROTEORISK-get-z">
          <ZAxis />
          {[[1.8, true], [0.6, false], [-2.3, true], [0.3, false]].map(([z, flagged]) => (
            <ZScoreBar key={z} z={z} flagged={flagged} />
          ))}
        </div>
        <h3>Proteins behind a finding</h3>
        <p>For moderate and high results, the proteins that weigh most, shown against their typical range.</p>
      </div>
    </div>
  );
}

export default function Info() {
  return (
    <div className="PROTEORISK-home">
      <Banner title="How ProteoRisk works" subtitle="What it does, how it works and what you get" />
      <div className="PROTEORISK-page">
        <section className="PROTEORISK-section PROTEORISK-hero">
          <div className="PROTEORISK-intro-copy">
            <span className="PROTEORISK-kicker">Blood protein analysis</span>
            <h2>See which conditions your blood protein pattern resembles</h2>
            <p>
              ProteoRisk reads the proteins measured in one blood sample and compares their pattern with
              patterns seen in people with 83 conditions. You get 129 results: how closely your proteins
              resemble those of people with each condition today, and of people who developed it within
              ten years.
            </p>
            <div className="PROTEORISK-hero-actions">
              <Link to="/proteorisk" className="PROTEORISK-button primary">
                <i className="fas fa-arrow-right" aria-hidden="true" /> Start an analysis
              </Link>
              <Link to="/proteorisk/example" className="PROTEORISK-button">
                <i className="fas fa-eye" aria-hidden="true" /> See an example report
              </Link>
              <Link to="/proteorisk/about" className="PROTEORISK-link">
                About ProteoRisk <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
            <div className="PROTEORISK-hero-stats">
              <div><strong>5,348</strong><span>proteins on the panel</span></div>
              <div><strong>129</strong><span>results</span></div>
              <div><strong>12</strong><span>body systems</span></div>
              <div><strong>0 to 100</strong><span>score for each result</span></div>
            </div>
          </div>
          <div className="PROTEORISK-hero-visual">
            <MiniWheel systems={EXAMPLE_WHEEL} className="large" />
            <div className="PROTEORISK-hero-legend">
              {['typical', 'moderate', 'high'].map((status) => (
                <span key={status}>
                  <StatusIcon status={status} size={12} />
                  {STATUS_LABEL[status]}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="PROTEORISK-section">
          <h2><i className="fas fa-route" aria-hidden="true" /> How it works</h2>
          <Steps />
        </section>

        <section className="PROTEORISK-section">
          <h2><i className="fas fa-gift" aria-hidden="true" /> What you get</h2>
          <GetTiles />
        </section>

        <div className="PROTEORISK-two-col">
          <section className="PROTEORISK-section">
            <h2><i className="fas fa-clipboard-list" aria-hidden="true" /> What you need</h2>
            <ul className="PROTEORISK-checklist">
              <li>
                <i className="fas fa-file-excel" aria-hidden="true" />
                <span>
                  One Olink Explore HT workbook (.xlsx) with a sheet named <strong>Data</strong>: a header row
                  that starts with DAid, barcode, Cohort and Diagnose, then one column per protein, and one row
                  with the sample&apos;s values.
                  {' '}
                  <ExampleWorkbookLink>See the example workbook</ExampleWorkbookLink>
                </span>
              </li>
              <li>
                <i className="fas fa-user" aria-hidden="true" />
                <span>Age in whole years and sex, so every protein can be adjusted to you.</span>
              </li>
              <li>
                <i className="fas fa-weight" aria-hidden="true" />
                <span>BMI if you know it. It is optional, and the analysis works without it.</span>
              </li>
            </ul>
          </section>
          <section className="PROTEORISK-section PROTEORISK-note-card">
            <h2><i className="fas fa-info-circle" aria-hidden="true" /> Good to know</h2>
            <p>
              ProteoRisk compares protein patterns. It is not a diagnostic test, and resembling a pattern is
              not the same as having the condition.
            </p>
            <p>
              Share your results with your doctor, who can look at them together with your health history
              and other test results.
            </p>
          </section>
        </div>

      </div>
    </div>
  );
}
